/**
 * Unified API client for the Sales Dashboard.
 *
 * All calls go to the self-hosted Odoo API addon at /dashboard/api/*.
 * The base URL is resolvable from three places, in order:
 *   1. localStorage('dashboard_server_url' + 'dashboard_api_base_path')
 *   2. import.meta.env.VITE_API_BASE_URL  (build-time override)
 *   3. '/dashboard/api'                   (relative — works with Vite proxy
 *                                          in dev, and same-host Nginx in prod)
 *
 * Authentication is by Odoo session cookie. Always send
 * `credentials: 'include'` so the cookie is attached.
 *
 * All responses are JSON; HTML error pages are intercepted and converted
 * to thrown ApiError objects so callers never see raw HTML.
 */

const LS_SERVER_URL = 'dashboard_server_url';
const LS_API_BASE = 'dashboard_api_base_path';
const LS_DB = 'dashboard_odoo_database';
const LS_BACKEND = 'sellgo_backend';

import { cacheGet, cacheSet, cacheLatestByPrefix } from '@/lib/offlineCache';

const isDev = import.meta.env?.DEV === true;

/**
 * Detect whether we're running inside a Capacitor (Android/iOS) shell.
 * In that case `window.location.origin` is `capacitor://localhost` or
 * `https://localhost`, which would make relative URLs hit the device.
 * We need absolute URLs pointing at the real production server.
 */
function isCapacitor() {
  if (typeof window === 'undefined') return false;
  return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
}

/**
 * Production server URL — used when running as a native app (Capacitor).
 * Can be overridden at build time via VITE_PROD_SERVER_URL.
 */
const PROD_SERVER_URL = (import.meta.env?.VITE_PROD_SERVER_URL || 'https://app.el-fath.co').replace(/\/$/, '');

/** Read base URL at call-time so changing it via Settings reflects immediately. */
export function getApiBaseUrl() {
  const server = (localStorage.getItem(LS_SERVER_URL) || '').replace(/\/$/, '');
  const basePath = (localStorage.getItem(LS_API_BASE) || 'dashboard/api').replace(/^\/|\/$/g, '');

  // Build-time override (e.g. .env): VITE_API_BASE_URL=https://api.example.com/dashboard/api
  const envBase = import.meta.env?.VITE_API_BASE_URL;
  if (envBase) return envBase.replace(/\/$/, '');

  // If user set a full server URL in Settings, prefer that.
  if (server) return `${server}/${basePath}`;

  // In a native app shell (Capacitor), `relative` would hit the device.
  // Force the production server URL instead.
  if (isCapacitor()) return `${PROD_SERVER_URL}/${basePath}`;

  // Default: relative — works with Vite proxy in dev and Nginx in prod.
  return `/${basePath}`;
}

/** The Odoo origin used for /web/* paths (login, logout, etc). */
export function getOdooOrigin() {
  const server = (localStorage.getItem(LS_SERVER_URL) || '').replace(/\/$/, '');
  if (server) return server;
  // Native app shell — always absolute.
  if (isCapacitor()) return PROD_SERVER_URL;
  // Same origin (relative) — let the Vite proxy or Nginx handle routing.
  return '';
}

/** The database name to send to /web/session/authenticate. */
export function getOdooDatabase() {
  return localStorage.getItem(LS_DB) || '';
}

/**
 * Which backend answers the API: 'odoo' (the customer's own Odoo server) or
 * 'supabase' (SellGo's own database, for customers without Odoo). A device
 * starts on the build's default (vite.config.js: SellGo's own database for
 * SellGo-Novanex; Odoo when not set) and switches with the ?connect=sellgo /
 * ?connect=odoo link or the hidden admin drawer on the login screen.
 */
const DEFAULT_BACKEND = typeof __DEFAULT_BACKEND__ !== 'undefined' && __DEFAULT_BACKEND__ === 'supabase' ? 'supabase' : 'odoo';

export function getBackend() {
  const chosen = localStorage.getItem(LS_BACKEND);
  return chosen === 'supabase' || chosen === 'odoo' ? chosen : DEFAULT_BACKEND;
}

export function setBackend(mode) {
  const next = mode === 'supabase' ? 'supabase' : 'odoo';
  if (next === DEFAULT_BACKEND) localStorage.removeItem(LS_BACKEND);
  else localStorage.setItem(LS_BACKEND, next);
}

export function setApiConfig({ serverUrl, apiBasePath, database }) {
  if (serverUrl !== undefined) {
    if (serverUrl) localStorage.setItem(LS_SERVER_URL, serverUrl.replace(/\/$/, ''));
    else localStorage.removeItem(LS_SERVER_URL);
  }
  if (apiBasePath !== undefined) {
    if (apiBasePath) localStorage.setItem(LS_API_BASE, apiBasePath.replace(/^\/|\/$/g, ''));
    else localStorage.removeItem(LS_API_BASE);
  }
  if (database !== undefined) {
    if (database) localStorage.setItem(LS_DB, database);
    else localStorage.removeItem(LS_DB);
  }
}

export class ApiError extends Error {
  constructor(message, { status = 0, data = null, url = '' } = {}) {
    super(message || 'Network error');
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.url = url;
  }
}

/**
 * Fired on `window` when Odoo answers as if we were logged out (the session
 * expired, or the server lost it — e.g. when its disk filled up).
 * OdooAuthProvider listens and sends the user back to the login screen.
 */
export const SESSION_EXPIRED_EVENT = 'sellgo:session-expired';

/**
 * Odoo bounces `auth='user'` routes with a 303 to /web/login, which fetch
 * follows — so we end up holding the login page instead of JSON. A 401 is
 * what a JSON-aware backend sends instead. Any other HTML (502 from nginx
 * while Odoo restarts, etc.) is NOT a logout: it stays a normal error.
 */
function isSessionExpired(res) {
  if (res.status === 401) return true;
  if (!res.redirected) return false;
  try {
    return new URL(res.url).pathname.startsWith('/web/login');
  } catch {
    return false;
  }
}

/** Build a query string from an object, dropping undefined / null / ''. */
function toQuery(params) {
  if (!params || typeof params !== 'object') return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => usp.append(k, String(x)));
    else usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

/** Core fetch wrapper. Returns parsed JSON or throws ApiError. */
export async function apiFetch(path, options = {}) {
  const {
    method = 'GET',
    body,
    params,
    headers = {},
    signal,
    skipBase = false,
    timeoutMs = 30000,
    skipCache = false,
  } = options;

  const onSupabase = getBackend() === 'supabase';
  const base = onSupabase ? 'sb:' : skipBase ? '' : getApiBaseUrl();
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}${toQuery(params)}`;

  // Offline read-through cache only applies to GETs.
  const useCache = method === 'GET' && !skipCache;
  const cacheKey = `GET ${url}`;
  const cachePathPrefix = `GET ${base}${path.startsWith('/') ? path : `/${path}`}`;

  if (onSupabase) {
    return supabaseApiFetch({ method, path, params, body, signal, timeoutMs, useCache, cacheKey, cachePathPrefix });
  }

  const init = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    signal,
  };
  if (body !== undefined) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  // Abort on timeout if no signal was passed
  let timeoutHandle;
  if (!signal && timeoutMs > 0) {
    const ctl = new AbortController();
    init.signal = ctl.signal;
    timeoutHandle = setTimeout(() => ctl.abort(new Error('Request timeout')), timeoutMs);
  }

  if (isDev) {
    // Lightweight dev logging — easy to grep for
    // eslint-disable-next-line no-console
    console.debug('[api]', method, url, body || '');
  }

  let res;
  try {
    res = await fetch(url, init);
  } catch (e) {
    if (timeoutHandle) clearTimeout(timeoutHandle);
    // Offline / network failure: fall back to the last cached copy of this GET.
    if (useCache) {
      const exact = await cacheGet(cacheKey);
      if (exact && 'v' in exact) return exact.v;
      const byPath = await cacheLatestByPrefix(cachePathPrefix);
      if (byPath !== undefined) return byPath;
    }
    throw new ApiError(`Network error: ${e.message || e}`, { url });
  }
  if (timeoutHandle) clearTimeout(timeoutHandle);

  if (isSessionExpired(res)) {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    throw new ApiError('انتهت الجلسة، سجّل الدخول مرة أخرى', { status: 401, url });
  }

  const text = await res.text();
  let data = null;
  let isJson = false;
  if (text) {
    try {
      data = JSON.parse(text);
      isJson = true;
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const msg =
      (isJson && data && (data.error || data.message)) ||
      (typeof data === 'string' && data.slice(0, 200)) ||
      `HTTP ${res.status}`;
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn('[api] FAIL', res.status, url, data);
    }
    throw new ApiError(msg, { status: res.status, data: isJson ? data : null, url });
  }

  if (!isJson) {
    // Server returned HTML (rare with our addon, but possible from a reverse proxy)
    throw new ApiError('Server returned non-JSON response', {
      status: res.status,
      data: { raw: typeof data === 'string' ? data.slice(0, 500) : null },
      url,
    });
  }

  // Save a copy of successful reads so they're available offline next time.
  // Skip error envelopes ({success:false}) so we never cache a failure.
  if (useCache && !(data && data.success === false)) {
    cacheSet(cacheKey, data);
  }

  return data;
}

/**
 * apiFetch on Supabase: same contract (JSON envelope or ApiError, offline
 * read-through cache for GETs, status 0 = network error), but the request is
 * answered by the Supabase router instead of fetch(). Loaded on demand so Odoo
 * installs never download the Supabase client.
 */
async function supabaseApiFetch({ method, path, params, body, signal, timeoutMs, useCache, cacheKey, cachePathPrefix }) {
  let timeoutHandle;
  let abortSignal = signal;
  if (!signal && timeoutMs > 0) {
    const ctl = new AbortController();
    abortSignal = ctl.signal;
    timeoutHandle = setTimeout(() => ctl.abort(), timeoutMs);
  }

  let data;
  try {
    const { supabaseFetch } = await import('./supabase/router');
    data = await supabaseFetch({ method, path, params, body, signal: abortSignal });
  } catch (e) {
    // A failed chunk download (offline before the Supabase code was cached) is a network error too.
    const err = e instanceof ApiError ? e : new ApiError(`Network error: ${e?.message || e}`, { url: `sb:${path}` });
    if (err.status === 401) {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    } else if (!err.status && useCache) {
      const exact = await cacheGet(cacheKey);
      if (exact && 'v' in exact) return exact.v;
      const byPath = await cacheLatestByPrefix(cachePathPrefix);
      if (byPath !== undefined) return byPath;
    }
    throw err;
  } finally {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }

  if (useCache && !(data && data.success === false)) {
    cacheSet(cacheKey, data);
  }
  return data;
}

/** Convenience verbs. All return the parsed JSON envelope. */
export const api = {
  get: (path, params, opts) => apiFetch(path, { ...opts, method: 'GET', params }),
  post: (path, body, opts) => apiFetch(path, { ...opts, method: 'POST', body }),
  put: (path, body, opts) => apiFetch(path, { ...opts, method: 'PUT', body }),
  patch: (path, body, opts) => apiFetch(path, { ...opts, method: 'PATCH', body }),
  del: (path, opts) => apiFetch(path, { ...opts, method: 'DELETE' }),
};

/**
 * Unwrap a `{success, data, error}` envelope and either return `data` or throw.
 *
 * Use when you don't care about pagination/extras and just want the rows.
 */
export async function fetchData(path, params, opts) {
  const env = await api.get(path, params, opts);
  if (env && env.success === false) {
    throw new ApiError(env.error || 'Request failed', { data: env });
  }
  return env?.data;
}

/* -------------------------------------------------------------------------
 *  Odoo session helpers (login / logout)
 *  These hit /web/* not /dashboard/api/*, so they bypass the API base URL.
 * ----------------------------------------------------------------------- */

function odooWebUrl(path) {
  const origin = getOdooOrigin();
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Authenticate via Odoo's standard session endpoint.
 *
 * Returns the parsed `result` object on success (containing `uid`, `name`,
 * `username`, `db`, `user_context`, etc.). Throws ApiError on failure.
 */
export async function odooAuthenticate({ db, login, password }) {
  if (getBackend() === 'supabase') {
    const { supabaseAuthenticate } = await import('./supabase/auth');
    return supabaseAuthenticate({ login, password });
  }
  const payload = {
    jsonrpc: '2.0',
    params: { db, login, password },
  };
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  // Prefer the addon's CORS-enabled login (lets one hosted frontend talk to
  // any tenant's Odoo directly). Fall back to Odoo's core route if the addon
  // endpoint isn't deployed yet. Both return the same { result: {...} } shape.
  const primary = `${getApiBaseUrl()}/session/authenticate`;
  const fallback = odooWebUrl('/web/session/authenticate');
  let url = primary;
  let res;
  try {
    res = await fetch(primary, {
      method: 'POST', credentials: 'include', headers, body: JSON.stringify(payload),
    });
    if (res.status === 404) throw new ApiError('addon endpoint missing', { status: 404 });
  } catch {
    url = fallback;
    res = await fetch(fallback, {
      method: 'POST', credentials: 'include', headers, body: JSON.stringify(payload),
    });
  }
  let json = null;
  try {
    json = await res.json();
  } catch {
    throw new ApiError('Odoo did not return JSON. Check the server URL.', {
      status: res.status,
      url,
    });
  }
  // Odoo returns 200 with {result: null} on bad credentials
  if (json.error) {
    throw new ApiError(json.error.data?.message || json.error.message || 'Login failed', {
      status: 401,
      data: json.error,
      url,
    });
  }
  if (!json.result || !json.result.uid) {
    throw new ApiError('Invalid credentials or user is not allowed on this database', {
      status: 401,
      data: json,
      url,
    });
  }
  return json.result;
}

/**
 * Destroy the Odoo session on the server, then clear local state.
 * Returns true if the destroy call succeeded (it's best-effort).
 */
export async function odooDestroySession() {
  if (getBackend() === 'supabase') {
    const { supabaseSignOut } = await import('./supabase/auth');
    return supabaseSignOut();
  }
  const body = JSON.stringify({ jsonrpc: '2.0', params: {} });
  const headers = { 'Content-Type': 'application/json' };
  const primary = `${getApiBaseUrl()}/session/destroy`;
  const fallback = odooWebUrl('/web/session/destroy');
  try {
    let res = await fetch(primary, { method: 'POST', credentials: 'include', headers, body });
    if (res.status === 404) {
      res = await fetch(fallback, { method: 'POST', credentials: 'include', headers, body });
    }
    return res.ok;
  } catch {
    return false;
  }
}
